// Shared document renderer used by both the Admin Documents panel and the
// public document share pages (/docs/:slug). The print CSS in index.css targets
// the .doc-page and .doc-single-page classes set here.

// Docs that must fit on a single printed page — get tighter print spacing.
export const SINGLE_PAGE_DOC_IDS = new Set([
  "partner-referral-guide",
  "police-referral-one-pager",
]);

const renderContent = (content) => {
  if (!content) return null;
  return content.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <br key={i} />;

    // Bold headers (ALL CAPS lines or lines ending with :)
    if (/^[A-Z][A-Z\s&()$,.\-/0-9]+:?$/.test(trimmed) && trimmed.length < 80) {
      return <p key={i} className="font-bold text-gray-900 mt-3 mb-1">{trimmed}</p>;
    }

    // Bullet points
    if (trimmed.startsWith("•") || trimmed.startsWith("-")) {
      const text = trimmed.replace(/^[•\-]\s*/, "");
      if (trimmed.startsWith("  ") || line.startsWith("  ")) {
        return <p key={i} className="pl-6 text-gray-500 text-[12px] mb-1">— {text}</p>;
      }
      return (
        <div key={i} className="flex gap-2 mb-1">
          <span className="text-warm-600 mt-0.5 flex-shrink-0">•</span>
          <span>{text}</span>
        </div>
      );
    }

    // Numbered items
    const numMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
    if (numMatch) {
      return (
        <div key={i} className="flex gap-3 mb-2">
          <div className="w-6 h-6 rounded-full bg-warm-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{numMatch[1]}</div>
          <span>{numMatch[2]}</span>
        </div>
      );
    }

    return <p key={i} className="mb-2">{trimmed}</p>;
  });
};

export const DocumentRenderer = ({ doc }) => {
  if (!doc) return null;
  const isSinglePage = SINGLE_PAGE_DOC_IDS.has(doc.id);

  return (
    <div
      className={`doc-page ${isSinglePage ? "doc-single-page" : ""} w-[850px] mx-auto bg-white`}
      style={{ fontFamily: "Georgia, serif" }}
      data-testid={`doc-renderer-${doc.id}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-warm-800 via-warm-700 to-warm-900 text-white px-12 py-10">
        <div className="flex items-center gap-4 mb-4">
          <img src="/logo-new.png" alt="Logo" className="w-14 h-14 object-contain bg-white/10 rounded-xl p-2" />
          <div>
            <h1 className="text-3xl font-bold leading-tight" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
              {doc.header_title || doc.title}
            </h1>
            {doc.header_subtitle && <p className="text-warm-300 text-sm mt-1">{doc.header_subtitle}</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6 text-sm border-t border-warm-600 pt-3">
          <div>
            <p className="text-warm-300 text-xs uppercase tracking-wider">Organization</p>
            <p className="font-semibold">The Pillar Project Corporation</p>
            <p className="text-warm-200 text-xs mt-0.5">Registered 501(c)(3) Tax-Exempt Nonprofit</p>
          </div>
          <div>
            <p className="text-warm-300 text-xs uppercase tracking-wider">Location</p>
            <p className="font-semibold">Baltimore, Maryland</p>
            <p className="text-warm-200 text-xs mt-0.5">director@thepillarprojectbaltimore.org</p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="px-12 py-8 space-y-6">
        {doc.sections?.map((section, i) => (
          <section key={i}>
            {section.heading && (
              <h2 className="text-lg font-bold text-warm-800 uppercase tracking-wider border-b-2 border-warm-800 pb-1 mb-3" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
                {section.heading}
              </h2>
            )}
            <div className="text-[14px] leading-relaxed text-gray-700">
              {renderContent(section.content)}
            </div>
            {section.content?.includes("Scan the QR code") && (
              <div className="mt-4 flex items-center gap-6 bg-warm-50 border border-warm-200 rounded-xl p-5">
                <img src="/qr-partner-portal.png" alt="Partner Portal QR Code" className="w-32 h-32" />
                <div>
                  <p className="font-bold text-warm-800 text-sm" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>Scan to Open Partner Portal</p>
                  <p className="text-xs text-gray-500 mt-1">thepillarprojectbaltimore.org/partner/login</p>
                  <p className="text-xs text-gray-400 mt-2">Register once, then submit referrals and LAP reports directly from your phone or computer.</p>
                </div>
              </div>
            )}
          </section>
        ))}

        {/* Footer */}
        <div className="border-t-2 border-warm-800 pt-4 mt-8 flex justify-between items-start text-[12px] text-gray-600">
          <div>
            <p className="font-bold text-gray-900 text-sm">The Pillar Project Corporation</p>
            <p>Registered 501(c)(3) Tax-Exempt Nonprofit</p>
            <p>Baltimore, Maryland</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-gray-900">(347) 221-4189</p>
            <p>director@thepillarprojectbaltimore.org</p>
            <p>www.thepillarprojectbaltimore.org</p>
          </div>
        </div>
      </div>
    </div>
  );
};
